import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Edit as EditIcon } from '@mui/icons-material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { CalendarMonth as CalendarMonthIcon } from '@mui/icons-material';
import { formatDate, formatFrequency } from './utils/recurrenceUtils';
import { getBudgetRecurrences, deleteBudgetRecurrence } from '../../services/budgetService';
import AddBudgetRecurrenceDialog from './AddBudgetRecurrenceDialog';

/**
 * Component for displaying and managing budget recurrences
 */
const BudgetRecurrenceList = () => {
  // State for recurrences data and UI
  const [recurrences, setRecurrences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRecurrence, setSelectedRecurrence] = useState(null);

  // Load recurrences on component mount
  useEffect(() => {
    fetchRecurrences();
  }, []);

  // Fetch recurrences from API
  const fetchRecurrences = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getBudgetRecurrences();
      setRecurrences(response.data);
    } catch (err) {
      setError('Failed to load budget recurrences. Please try again.');
      console.error('Error fetching budget recurrences:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening add dialog
  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  // Handle closing add dialog
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  // Handle recurrence added successfully
  const handleRecurrenceAdded = () => {
    fetchRecurrences();
  };

  // Handle opening delete confirmation dialog
  const handleOpenDeleteDialog = (recurrence) => {
    setSelectedRecurrence(recurrence);
    setShowDeleteDialog(true);
  };

  // Handle closing delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setSelectedRecurrence(null);
  };

  // Handle delete recurrence
  const handleDeleteRecurrence = async () => {
    if (!selectedRecurrence) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await deleteBudgetRecurrence(selectedRecurrence._id);
      await fetchRecurrences();
      handleCloseDeleteDialog();
    } catch (err) {
      setError('Failed to delete budget recurrence. Please try again.');
      console.error('Error deleting budget recurrence:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit recurrence (to be implemented)
  const handleEditRecurrence = (recurrence) => {
    // Implementation for editing a recurrence would go here
    console.log('Edit recurrence:', recurrence);
  };

  // Render recurrence status chip
  const renderStatusChip = (recurrence) => {
    const now = new Date();
    const startDate = new Date(recurrence.startDate);
    
    if (startDate > now) {
      return <Chip label="Upcoming" color="info" size="small" />;
    }
    
    return <Chip label="Active" color="success" size="small" />;
  };

  return (
    <Box>
      <Card>
        <CardHeader
          title="Budget Recurrences"
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              disabled={loading}
            >
              Add Recurrence
            </Button>
          }
        />
        
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading && recurrences.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : recurrences.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                No budget recurrences found. Create your first recurrence to automate budget creation.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Frequency</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recurrences.map((recurrence) => (
                    <TableRow key={recurrence._id}>
                      <TableCell>
                        <Typography variant="body1">{recurrence.name}</Typography>
                      </TableCell>
                      <TableCell>
                        {recurrence.template ? recurrence.template.name : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CalendarMonthIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatFrequency({
                              frequency: recurrence.frequency,
                              dayOfWeek: recurrence.dayOfWeek,
                              dayOfMonth: recurrence.dayOfMonth
                            })}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{formatDate(recurrence.startDate)}</TableCell>
                      <TableCell>{renderStatusChip(recurrence)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditRecurrence(recurrence)}
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleOpenDeleteDialog(recurrence)}
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      
      {/* Add Budget Recurrence Dialog */}
      <AddBudgetRecurrenceDialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        onSuccess={handleRecurrenceAdded}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Budget Recurrence</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the budget recurrence "{selectedRecurrence?.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteRecurrence} 
            color="error" 
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BudgetRecurrenceList; 